import { useCallback } from 'react'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { isDraftCreateNewAction } from '../../Draft.utils'
import { useDraftCreationModalOpener } from '../useDraftCreationModalOpener'

import {
  getDraftCreationInitialValues,
  getDraftCreationSubmitPayload,
} from './useDraftCreationOpener.utils'

// Generic, entity-agnostic creation opener. Owns:
// - picker-vs-direct decision when a draft exists
// - action dispatch (Create new / Open draft) into entity-specific callbacks
// - minimize-aware open via `useDraftCreationModalOpener`
//
// Entity-specific behavior (which modal to render, wizard side effects) lives
// in the caller's `openModal` / `onCreateNew` / `onOpenDraft` closures.
export function useDraftCreationOpener({
  DraftPickerModal,
  closeModal,
  draft,
  draftId,
  getInitialValues = getDraftCreationInitialValues,
  getSubmitPayload = getDraftCreationSubmitPayload,
  minimizeKey,
  openModal,
  setModal,
  onCreateNew,
  onOpenDraft,
}) {
  const openWithAction = useCallback(
    payload => {
      if (isDraftCreateNewAction(payload.action)) {
        onCreateNew?.(payload)
      } else {
        onOpenDraft?.(payload, draft)
      }

      openModal(payload)
    },
    [draft, onCreateNew, onOpenDraft, openModal]
  )

  const handlePickerSubmit = useCallback(
    (payload, { action, initialValues }) => {
      closeModal()
      openWithAction({
        ...getSubmitPayload(payload, initialValues),
        action,
      })
    },
    [closeModal, getSubmitPayload, openWithAction]
  )

  const handleOpen = useCallback(
    (payload = EMPTY_OBJECT) => {
      if (payload.action != null) {
        openWithAction(payload)

        return
      }

      if (draft) {
        setModal(
          <DraftPickerModal
            draft={draft}
            initialValues={getInitialValues(payload)}
            onCancel={closeModal}
            onSubmit={result => handlePickerSubmit(payload, result)}
          />
        )

        return
      }

      openModal(payload)
    },
    [
      closeModal,
      draft,
      getInitialValues,
      handlePickerSubmit,
      openModal,
      openWithAction,
      setModal,
    ]
  )

  return useDraftCreationModalOpener({
    minimizeKey,
    sourceId: draftId,
    onOpen: handleOpen,
  })
}
